package com.evoapps.evoshift.domain.staffimport

import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.salesimport.SalesFileFormat
import com.evoapps.evoshift.domain.salesimport.SalesSheetParser
import com.evoapps.evoshift.domain.usecase.reports.ExportForAccountantUseCase

/** A person as the import will add them (the employee form's fields for someone new). */
data class StaffCandidate(
    val displayName: String,
    val email: String? = null,
    /** Normalised: "+48 600 100 200" for Polish mobiles, else "+" and the digits, or the digits. */
    val phone: String? = null,
    val role: String = "",
    val permission: UserPermission = UserPermission.EMPLOYEE,
    val contract: SystemContractType = SystemContractType.NOT_SET,
    val isHourly: Boolean = true,
    /** The hourly rate or the monthly salary ([isHourly]); null: none given. */
    val rate: Double? = null,
    val student: Boolean = false,
    val shiftLead: Boolean = false,
    /** The workplace's shift lead skill when [shiftLead] (and the workplace has one). */
    val skills: List<String> = emptyList()
)

/** What stops a row from being imported (the UI words it; the person fixes or skips the row). */
enum class StaffRowProblem {
    NAME_MISSING,
    EMAIL_INVALID,
    /** An earlier row of the file has the same e-mail. */
    EMAIL_DUPLICATE_IN_FILE,
    /** Someone in this workplace already has this e-mail. */
    EMAIL_ALREADY_IN_TEAM,
    PHONE_INVALID,
    PERMISSION_UNKNOWN,
    /** An owner, or a permission above the importer's own (firestore.rules refuses it). */
    PERMISSION_NOT_ALLOWED,
    CONTRACT_UNKNOWN,
    PAY_MODEL_UNKNOWN,
    RATE_NOT_A_NUMBER,
    RATE_NEGATIVE,
    STUDENT_UNKNOWN,
    SHIFT_LEAD_UNKNOWN
}

/** Worth knowing, but the row imports. */
enum class StaffRowNote {
    /** Someone in this workplace has the same name (maybe the same person). */
    NAME_ALREADY_IN_TEAM,
    /** An earlier row has the same name. */
    NAME_DUPLICATE_IN_FILE,
    /** A position the workplace does not list yet (it is saved as typed). */
    NEW_ROLE,
    /** Marked as a shift lead, but the workplace marks no skill as leading shifts. */
    SHIFT_LEAD_NOT_CONFIGURED
}

/** A row checked: the person it makes and what is wrong with it. */
data class StaffRowCheck(
    val row: StaffImportRow,
    val candidate: StaffCandidate,
    val problems: List<StaffRowProblem>,
    val notes: List<StaffRowNote> = emptyList()
) {
    val isValid: Boolean get() = problems.isEmpty()
}

/** What the rows are checked against. */
data class StaffImportContext(
    /** The permissions the importer may give (none above their own). */
    val allowedPermissions: Set<UserPermission> = setOf(UserPermission.EMPLOYEE),
    /** The workplace's positions: a matching cell takes their spelling. */
    val workplaceRoles: List<String> = emptyList(),
    /** Positions whose holders lead shifts (no shift lead skill needed for them). */
    val leadRoles: List<String> = emptyList(),
    /** The skill that makes someone a shift lead (the first the workplace marks), if any. */
    val leadSkill: String? = null,
    /** E-mails of the people already in the workplace, lower case (empty when unknown). */
    val teamEmails: Set<String> = emptySet(),
    /** Names of the people already in the workplace. */
    val teamNames: Collection<String> = emptyList(),
    /** The country calling code for numbers without one (+48 in Poland); null: keep as written. */
    val phoneCountryCode: String? = "48",
    /** Numbers as an .xlsx stores them ("1234.5"), not as a person types them. */
    val numbersAsStored: Boolean = false
)

object StaffImportValidator {

    /**
     * Checks [rows] (the [skipped] row numbers are left out of the duplicate checks, and come back
     * checked anyway so the person can bring them back).
     */
    fun check(rows: List<StaffImportRow>, context: StaffImportContext, skipped: Set<Int> = emptySet()): List<StaffRowCheck> {
        val teamNames = context.teamNames.mapTo(HashSet()) { nameKey(it) }
        val seenEmails = HashSet<String>()
        val seenNames = HashSet<String>()
        return rows.map { row ->
            val check = checkRow(row, context)
            if (row.number in skipped) return@map check
            val problems = check.problems.toMutableList()
            val notes = check.notes.toMutableList()
            check.candidate.email?.let { email ->
                if (email in context.teamEmails) problems += StaffRowProblem.EMAIL_ALREADY_IN_TEAM
                else if (!seenEmails.add(email)) problems += StaffRowProblem.EMAIL_DUPLICATE_IN_FILE
            }
            val name = nameKey(check.candidate.displayName)
            if (name.isNotEmpty()) {
                if (name in teamNames) notes += StaffRowNote.NAME_ALREADY_IN_TEAM
                else if (!seenNames.add(name)) notes += StaffRowNote.NAME_DUPLICATE_IN_FILE
            }
            check.copy(problems = problems, notes = notes)
        }
    }

    /** One row on its own (no duplicate checks). */
    fun checkRow(row: StaffImportRow, context: StaffImportContext): StaffRowCheck {
        val problems = mutableListOf<StaffRowProblem>()
        val notes = mutableListOf<StaffRowNote>()

        val name = row[StaffImportField.NAME].ifEmpty {
            listOf(row[StaffImportField.FIRST_NAME], row[StaffImportField.LAST_NAME]).filter { it.isNotEmpty() }.joinToString(" ")
        }.split(' ', '\t', ' ').filter { it.isNotEmpty() }.joinToString(" ")
        if (name.isEmpty()) problems += StaffRowProblem.NAME_MISSING

        val emailCell = row[StaffImportField.EMAIL]
        val email = emailCell.takeIf { it.isNotEmpty() }?.let { cell ->
            StaffValues.email(cell).also { if (it == null) problems += StaffRowProblem.EMAIL_INVALID }
        }

        val phoneCell = row[StaffImportField.PHONE]
        val phone = phoneCell.takeIf { it.isNotEmpty() }?.let { cell ->
            StaffValues.phone(cell, context.phoneCountryCode).also { if (it == null) problems += StaffRowProblem.PHONE_INVALID }
        }

        val roleCell = row[StaffImportField.ROLE]
        val knownRole = context.workplaceRoles.firstOrNull { it.trim().equals(roleCell, ignoreCase = true) }
        val role = knownRole?.trim() ?: roleCell
        if (roleCell.isNotEmpty() && context.workplaceRoles.isNotEmpty() && knownRole == null) notes += StaffRowNote.NEW_ROLE

        val permission = row[StaffImportField.PERMISSION].let { cell ->
            if (cell.isEmpty()) UserPermission.EMPLOYEE
            else when (val p = StaffValues.permission(cell)) {
                null -> { problems += StaffRowProblem.PERMISSION_UNKNOWN; UserPermission.EMPLOYEE }
                else -> {
                    if (p == UserPermission.OWNER || p !in context.allowedPermissions) problems += StaffRowProblem.PERMISSION_NOT_ALLOWED
                    p
                }
            }
        }

        val contract = row[StaffImportField.CONTRACT].let { cell ->
            if (cell.isEmpty()) SystemContractType.NOT_SET
            else StaffValues.contract(cell) ?: SystemContractType.NOT_SET.also { problems += StaffRowProblem.CONTRACT_UNKNOWN }
        }

        val isHourly = row[StaffImportField.PAY_MODEL].let { cell ->
            if (cell.isEmpty()) true else StaffValues.isHourly(cell) ?: true.also { problems += StaffRowProblem.PAY_MODEL_UNKNOWN }
        }

        val rate = row[StaffImportField.RATE].takeIf { it.isNotEmpty() }?.let { cell ->
            when (val value = StaffValues.amount(cell, context.numbersAsStored)) {
                null -> { problems += StaffRowProblem.RATE_NOT_A_NUMBER; null }
                else -> if (value < 0) { problems += StaffRowProblem.RATE_NEGATIVE; null } else value
            }
        }

        val student = row[StaffImportField.STUDENT].let { cell ->
            if (cell.isEmpty()) false else StaffValues.yesNo(cell) ?: false.also { problems += StaffRowProblem.STUDENT_UNKNOWN }
        }

        val shiftLead = row[StaffImportField.SHIFT_LEAD].let { cell ->
            if (cell.isEmpty()) false else StaffValues.yesNo(cell) ?: false.also { problems += StaffRowProblem.SHIFT_LEAD_UNKNOWN }
        }
        val leadsByRole = context.leadRoles.any { it.trim().equals(role, ignoreCase = true) }
        val skills = if (shiftLead && !leadsByRole) {
            context.leadSkill?.let { listOf(it) } ?: emptyList<String>().also {
                // A manager leads shifts anyway.
                if (permission == UserPermission.EMPLOYEE) notes += StaffRowNote.SHIFT_LEAD_NOT_CONFIGURED
            }
        } else emptyList()

        val candidate = StaffCandidate(
            displayName = name,
            email = email,
            phone = phone,
            role = role,
            permission = permission,
            contract = contract,
            isHourly = isHourly,
            rate = rate,
            student = student,
            shiftLead = shiftLead,
            skills = skills
        )
        return StaffRowCheck(row, candidate, problems, notes)
    }

    private fun nameKey(name: String): String = StaffHeaders.normalise(name)
}

/** How the import reads the cells people write. */
object StaffValues {

    /** Lower case, or null when not an e-mail address (the server's check). */
    fun email(cell: String): String? {
        val value = cell.trim().removePrefix("mailto:").lowercase()
        return value.takeIf { ExportForAccountantUseCase.isEmailAddress(it) }
    }

    /**
     * A phone number as written ("600 100 200", "+48 600-100-200", "(0048) 600100200", or a number
     * cell 48600100200): spaces, dashes, dots, slashes and brackets dropped; "00" is "+". Nine
     * digits without a country code get [countryCode] (Polish numbers have nine). Null: letters,
     * or fewer than 7 or more than 15 digits.
     */
    fun phone(cell: String, countryCode: String?): String? {
        val text = cell.trim()
        if (text.any { !(it.isDigit() || it in " -./()+ ") }) return null
        var digits = text.filter { it.isDigit() }
        var international = text.trimStart('(').startsWith("+")
        if (!international && digits.startsWith("00")) { digits = digits.drop(2); international = true }
        if (!international && countryCode != null && digits.length == 9 + countryCode.length && digits.startsWith(countryCode)) international = true
        if (!international && countryCode != null && digits.length == 9) { digits = countryCode + digits; international = true }
        if (digits.length !in 7..15) return null
        if (!international) return digits
        // +48 600 100 200: how Polish numbers are written.
        if (countryCode != null && digits.length == 9 + countryCode.length && digits.startsWith(countryCode)) {
            val local = digits.drop(countryCode.length)
            return "+$countryCode ${local.take(3)} ${local.substring(3, 6)} ${local.drop(6)}"
        }
        return "+$digits"
    }

    fun permission(cell: String): UserPermission? {
        val key = StaffHeaders.normalise(cell)
        fun has(vararg parts: String) = parts.any { it in key }
        return when {
            key.isEmpty() -> null
            has("finans", "financ", "фінанс") -> UserPermission.MANAGER_FINANCIAL
            has("wlasciciel", "owner", "власник") -> UserPermission.OWNER
            has("kierown", "manag", "menedz", "menadz", "керівн", "менедж") -> UserPermission.MANAGER
            has("pracown", "employee", "staff", "worker", "працівн") -> UserPermission.EMPLOYEE
            else -> null
        }
    }

    fun contract(cell: String): SystemContractType? {
        val key = StaffHeaders.normalise(cell)
        fun has(vararg parts: String) = parts.any { it in key }
        return when {
            key.isEmpty() -> null
            key == "uop" || has("oprace", "employment", "трудов") -> SystemContractType.STANDARD_EMPLOYMENT
            key == "uz" || has("zlecen", "civil", "mandate", "доручен") -> SystemContractType.CIVIL_LAW_CONTRACT
            key == "uod" || has("dzielo", "specifictask", "taskcontract", "підряд") -> SystemContractType.SPECIFIC_TASK_CONTRACT
            has("b2b", "dzialalnosc", "faktur", "selfemployed", "contractor", "invoice") -> SystemContractType.B2B_CONTRACTOR
            has("wlasciciel", "wspolnik", "owner", "partner", "власник", "партнер") -> SystemContractType.OWNER_PARTNER
            else -> SystemContractType.fromId(cell).takeIf { it != SystemContractType.NOT_SET }
        }
    }

    /** True: hourly, false: a monthly salary, null: not recognised. */
    fun isHourly(cell: String): Boolean? {
        val key = StaffHeaders.normalise(cell)
        fun has(vararg parts: String) = parts.any { it in key }
        return when {
            key.isEmpty() -> null
            key == "h" || has("godzin", "godz", "hour", "погодин", "годин") -> true
            has("miesi", "pensj", "etat", "month", "salary", "fixed", "місяч", "оклад") -> false
            else -> null
        }
    }

    /** "31,50", "31.50 zł", "PLN 6 500", "31,50 zł/h", a number cell; null when no number. */
    fun amount(cell: String, numbersAsStored: Boolean = false): Double? {
        val text = cell.trim()
        if (numbersAsStored) SalesSheetParser.parseAmount(text, SalesFileFormat.XLSX)?.let { return it }
        // The number itself: from its first digit (or minus) to the first letter or "/" after it.
        val start = text.indexOfFirst { it.isDigit() || it == '-' }
        if (start < 0) return null
        if (text.substring(0, start).any { it.isDigit() }) return null
        val end = (start until text.length).firstOrNull { i -> text[i].isLetter() || text[i] == '/' } ?: text.length
        val number = text.substring(start, end).trim()
        val rest = text.substring(end).lowercase().filter { it.isLetter() }
        // After the number only a currency and "/h", "/godz.", "na godzinę", "per hour" and the like.
        if (rest.isNotEmpty() && UNIT_WORDS.none { rest == it }) return null
        return SalesSheetParser.parseAmount(number, SalesFileFormat.CSV)
    }

    private val UNIT_WORDS: List<String> = run {
        val currencies = listOf("", "zł", "zl", "pln", "eur", "€", "usd", "gbp", "грн", "uah")
        val units = listOf("", "h", "godz", "godzina", "godzine", "nagodzine", "zagodzine", "hour", "perhour", "hr", "mies", "miesiac", "month", "permonth", "brutto", "netto", "gross", "net", "год")
        currencies.flatMap { c -> units.map { u -> c + u } }.filter { it.isNotEmpty() }.map { it.filter(Char::isLetter) }
    }

    /** Yes/no as people write it (tak, x, 1, TRUE, ✓; nie, 0, FALSE, -); null when neither. */
    fun yesNo(cell: String): Boolean? {
        val text = cell.trim()
        if (text in setOf("✓", "✔", "☑", "+", "x", "X")) return true
        if (text in setOf("-", "–", "✗", "✘")) return false
        return when (StaffHeaders.normalise(text)) {
            "tak", "t", "yes", "y", "true", "prawda", "1", "так", "да" -> true
            "nie", "n", "no", "false", "falsz", "0", "ні", "нет" -> false
            else -> null
        }
    }
}
